/**
 * Forms Module - Main entry point
 */

import { showLoader, hideLoader } from './loader';
import { runCaptcha, resetCaptcha } from './captcha';
import { hideModal, showModal, showErrorModalWithReopen } from './modals';
import { clearErrorBlock, populateErrorBlock, populateErrorUnderInput, findErrorBlock, type ErrorResponse } from './errors';
import { validateStepInputs, setupValidationEvents } from './validation';
import { isMultiStepForm, getCurrentStep, getTotalSteps, showFinalStep, setupMultistepEvents, type MultistepButtons } from './multistep';
import { serializeForm, submitRequest, refreshCsrfToken, resetFormFields, isSuccessResponse, fireLinkedInPixel, type SuccessResponse } from './submission';
import { initializePhoneInput, setupPhoneEvents, mapLocalizationToCountry } from './phone';

/**
 * Configuration options for FormHandler
 */
export interface FormHandlerOptions {
    type?: string;
    async?: boolean;
    contentType?: string;
    timeout?: number;
    processData?: boolean;
    dataType?: 'json' | 'text';
    validate?: boolean;
    successModal?: string;
    currentModal?: string;
    thankYouUrl?: string;
    thankYouMeetingUrl?: string;
    errorModal?: string;
    errorBlockClass?: string;
    scrollOnError?: boolean;
    expectSuccess?: boolean;
    resetFields?: boolean;
    componentId?: string | null;
    linkedinPixel?: string | null;
    formHasFiles?: boolean;
    additionalValidationPassed?: boolean;
    forceCaptcha?: boolean;
    beforeSubmitCall?: (() => void) | null;
    onSuccess?: ((response: SuccessResponse) => void) | null;
    onFail?: ((error: ErrorResponse) => void) | null;
    initialCountry?: string | null;
    url?: string | null;
    data?: FormData | string | null;
}

/**
 * Default form settings
 */
const DEFAULTS: Required<FormHandlerOptions> = {
    type: 'POST',
    async: true,
    contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
    timeout: 60000,
    processData: true,
    dataType: 'json',
    validate: false,
    successModal: '#requestSuccessModal',
    currentModal: '',
    thankYouUrl: '',
    thankYouMeetingUrl: '',
    errorModal: '#requestFailedTryAgain',
    errorBlockClass: '.error-block',
    scrollOnError: true,
    expectSuccess: true,
    resetFields: true,
    componentId: null,
    linkedinPixel: null,
    formHasFiles: false,
    additionalValidationPassed: true,
    forceCaptcha: true,
    beforeSubmitCall: null,
    onSuccess: null,
    onFail: null,
    initialCountry: null,
    url: null,
    data: null
};

/**
 * intl-tel-input instance interface
 */
interface IntlTelInputInstance {
    getSelectedCountryData(): { iso2: string; dialCode: string; name: string };
    destroy(): void;
}

/**
 * Country data from intl-tel-input globals
 */
interface CountryData {
    iso2: string;
    dialCode: string;
    name: string;
}

declare const intlTelInputGlobals: {
    getCountryData(): CountryData[];
};

/**
 * FormHandler - Form submission handler
 * Orchestrates form validation, submission, multi-step forms, and modals
 */
class FormHandler {
    private readonly form: HTMLFormElement;
    private readonly type: string;
    private readonly contentType: string;
    private readonly timeout: number;
    private readonly dataType: 'json' | 'text';
    private readonly validate: boolean;
    private readonly successModal: string;
    private readonly currentModal: string;
    private readonly thankYouUrl: string;
    private readonly thankYouMeetingUrl: string;
    private readonly errorModal: string;
    private readonly scrollOnError: boolean;
    private readonly expectSuccess: boolean;
    private readonly resetFields: boolean;
    private readonly componentId: string | null;
    private readonly linkedinPixel: string | null;
    private readonly formHasFiles: boolean;
    private readonly forceCaptcha: boolean;
    private readonly beforeSubmitCall: (() => void) | null;
    private readonly callback: ((response: SuccessResponse) => void) | null;
    private readonly failCallback: ((error: ErrorResponse) => void) | null;

    private additionalValidationPassed: boolean;
    private event: Event | null = null;
    private data: FormData | string;
    private submitBtn: HTMLButtonElement | null = null;
    private nextBtn: HTMLButtonElement | null = null;
    private prevBtn: HTMLButtonElement | null = null;
    private readonly isMultiStep: boolean;
    private buttonText: string | null = null;
    private readonly errorBlock: HTMLElement | null;
    private phoneInstance: IntlTelInputInstance | null = null;
    private readonly url: string;

    constructor(form: HTMLFormElement, options: FormHandlerOptions = {}) {
        this.form = form;
        const settings = { ...DEFAULTS, ...options };

        // Configuration Mapping
        this.type = settings.type;
        this.contentType = settings.contentType;
        this.timeout = settings.timeout;
        this.dataType = settings.dataType;
        this.validate = settings.validate;
        this.successModal = settings.successModal;
        this.currentModal = settings.currentModal;
        this.thankYouUrl = settings.thankYouUrl;
        this.thankYouMeetingUrl = settings.thankYouMeetingUrl;
        this.errorModal = settings.errorModal;
        this.scrollOnError = settings.scrollOnError;
        this.expectSuccess = settings.expectSuccess;
        this.resetFields = settings.resetFields;
        this.componentId = settings.componentId;
        this.linkedinPixel = settings.linkedinPixel;
        this.formHasFiles = settings.formHasFiles;
        this.additionalValidationPassed = settings.additionalValidationPassed;
        this.forceCaptcha = settings.forceCaptcha;
        this.beforeSubmitCall = settings.beforeSubmitCall;
        this.callback = settings.onSuccess;
        this.failCallback = settings.onFail;

        this.url = settings.url || form.getAttribute('action') || '';
        this.data = settings.data || serializeForm(form, this.formHasFiles);
        this.isMultiStep = isMultiStepForm(form);
        this.errorBlock = findErrorBlock(this.currentModal, settings.errorBlockClass);

        this.init(settings.initialCountry);
    }

    private init(initialCountry: string | null): void {
        this.initializeEvents();
        this.initializePhoneInput(initialCountry);
    }

    private initializePhoneInput(initialCountry: string | null): void {
        if (!initialCountry) {
            return;
        }

        const countryData = typeof intlTelInputGlobals !== 'undefined'
            ? intlTelInputGlobals.getCountryData()
            : [];

        const country = mapLocalizationToCountry(initialCountry, countryData);
        this.phoneInstance = initializePhoneInput(this.form, country);

        if (this.phoneInstance) {
            setupPhoneEvents(this.form, this.phoneInstance);
        }
    }

    private initializeEvents(): void {
        this.submitBtn = this.form.querySelector<HTMLButtonElement>('button[type="submit"]');

        // Setup Live Validation
        setupValidationEvents(this.form, () => {
            this.updateSubmitButtonState();
        });

        this.initializeSubmitEvents();

        if (this.isMultiStep) {
            const buttons: MultistepButtons = {
                submitBtn: this.submitBtn,
                nextBtn: null,
                prevBtn: null
            };

            setupMultistepEvents(this.form, buttons, () => {
                this.updateSubmitButtonState();
            });

            this.nextBtn = buttons.nextBtn;
            this.prevBtn = buttons.prevBtn;
        }
    }

    private initializeSubmitEvents(): void {
        if (!this.componentId) {
            return;
        }

        if (this.submitBtn) {
            this.submitBtn.addEventListener('click', (e: Event) => {
                e.preventDefault();

                if (this.forceCaptcha) {
                    const captchaExecuted = runCaptcha(this.componentId);

                    if (captchaExecuted) {
                        return;
                    }
                }

                this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            });
        }

        this.form.addEventListener('submit', (e: Event) => {
            e.preventDefault();
            this.resetData();
            this.event = e;

            if (this.beforeSubmitCall) {
                this.beforeSubmitCall();
            }

            if (this.validateForm()) {
                this.submitForm();
                fireLinkedInPixel(this.componentId, this.linkedinPixel);
            } else {
                resetCaptcha(this.componentId);
            }
        });
    }

    private validateForm(): boolean {
        if (this.validate === false) {
            return true;
        }

        const valid = validateStepInputs(this.form);

        if (valid === false || this.additionalValidationPassed === false) {
            if (this.event) {
                this.event.preventDefault();
                this.event.stopPropagation();
            }
            this.form.classList.add('was-validated');
            return false;
        }

        return true;
    }

    private updateSubmitButtonState(): boolean {
        if (this.validate === false) {
            return true;
        }

        let button: HTMLButtonElement | null;
        const currentStep = getCurrentStep(this.form);
        const totalSteps = getTotalSteps(this.form);

        if (this.isMultiStep && currentStep < (totalSteps - 1)) {
            button = this.nextBtn;
        } else {
            button = this.submitBtn;
        }

        const stepSelector = this.isMultiStep
            ? this.form.querySelectorAll<HTMLElement>('.step-content > div')[currentStep]
            : this.form;

        const valid = validateStepInputs(stepSelector);

        if (button) {
            button.disabled = (valid === false) || (this.additionalValidationPassed === false);
        }

        return valid && this.additionalValidationPassed;
    }

    private resetData(): void {
        this.data = serializeForm(this.form, this.formHasFiles);
        this.updateSubmitButtonState();
    }

    private submitForm(): void {
        if (this.event) {
            this.event.preventDefault();
        }

        clearErrorBlock(this.errorBlock);

        if (this.validateForm() === false) {
            return;
        }

        this.buttonText = showLoader(this.submitBtn);

        submitRequest<SuccessResponse>({
            url: this.url,
            method: this.type,
            data: this.data,
            contentType: this.contentType,
            timeout: this.timeout,
            hasFiles: this.formHasFiles,
            dataType: this.dataType
        })
            .then((response) => {
                this.handleAlways();
                this.handleDone(response);
            })
            .catch((error: ErrorResponse) => {
                this.handleAlways();
                this.handleFail(error);

                if (this.failCallback) {
                    this.failCallback(error);
                }
            });
    }

    private handleDone(response: SuccessResponse | string): void {
        let responseObj: SuccessResponse;

        if (this.dataType !== 'json' && typeof response === 'string') {
            responseObj = JSON.parse(response);
        } else {
            responseObj = response as SuccessResponse;
        }

        if (this.expectSuccess && isSuccessResponse(responseObj) === false) {
            if (responseObj.message) {
                this.showErrors(responseObj);
            }
            return;
        }

        if (this.callback) {
            this.callback(responseObj);
        }

        if (this.resetFields) {
            resetFormFields(this.form, this.submitBtn);
        }

        if (this.thankYouUrl) {
            window.location.href = this.thankYouMeetingUrl || this.thankYouUrl;
            return;
        }

        if (this.isMultiStep) {
            showFinalStep(this.form, 'success');
            return;
        }

        hideModal(this.currentModal);
        showModal(this.successModal);
    }

    private handleFail(response: ErrorResponse | string): void {
        let errorResponse: ErrorResponse;

        if (this.dataType !== 'json' && typeof response === 'string') {
            errorResponse = JSON.parse(response);
        } else {
            errorResponse = response as ErrorResponse;
        }

        console.error('Ajax call to ' + this.url + ' failed:', errorResponse.responseText || errorResponse);

        if (this.isMultiStep) {
            showFinalStep(this.form, 'failed');
            return;
        }

        showErrorModalWithReopen(this.errorModal, this.currentModal);
        resetCaptcha(this.componentId);

        if (errorResponse) {
            this.showErrors(errorResponse);
        }
    }

    private handleAlways(): void {
        this.form.classList.remove('was-validated');
        hideLoader(this.submitBtn, this.buttonText);
        resetCaptcha(this.componentId);
        refreshCsrfToken();
    }

    private showErrors(response: ErrorResponse): void {
        if (this.errorBlock) {
            populateErrorBlock(this.errorBlock, response, this.scrollOnError);
        } else {
            populateErrorUnderInput(this.form, response);
        }
    }
}

(window as unknown as { FormHandler: typeof FormHandler }).FormHandler = FormHandler;

export { FormHandler };